'use client'

import type { ReactNode } from 'react'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import type { FieldMode } from './types'

/**
 * Premium control styling (U2), applied via className on the SHARED field
 * components only — never on the global `ui/input.tsx` (that would restyle
 * every other dashboard form). `cn`/tailwind-merge resolves the conflicting
 * base classes (h-9 → h-10, rounded-md → rounded-lg, ring-1 → ring-2).
 *
 * iOS-zoom guard: 16px text on mobile (`text-base`), tightening to 14px at
 * `sm:` — base default, not the reverse.
 */
export const premiumControlClass =
  'h-10 rounded-lg text-base transition-[color,box-shadow] duration-150 placeholder:text-muted-foreground/70 focus-visible:ring-2 focus-visible:ring-ring/40 sm:text-sm'

/** Textarea variant — auto-height with a comfortable minimum, same uplift. */
export const premiumTextareaClass =
  'min-h-24 rounded-lg text-base transition-[color,box-shadow] duration-150 placeholder:text-muted-foreground/70 focus-visible:ring-2 focus-visible:ring-ring/40 sm:text-sm'

export function FieldLabel({ htmlFor, label, required }: { htmlFor?: string; label?: string; required?: boolean }) {
  if (!label) return null
  return (
    <Label htmlFor={htmlFor} className="block">
      {label}
      {required && <span className="ml-1 text-destructive">*</span>}
    </Label>
  )
}

export function FieldHelpText({ helpText }: { helpText?: string }) {
  if (!helpText) return null
  return <p className="text-xs text-muted-foreground">{helpText}</p>
}

export function FieldError({ error }: { error?: string }) {
  if (!error) return null
  return <p className="text-xs text-destructive">{error}</p>
}

/** Builder-preview marker for `showIf`-conditional fields. */
export function ConditionalBadge() {
  return (
    <span className="rounded-full border border-dashed px-1.5 text-[10px] uppercase tracking-wide text-muted-foreground">
      Shown conditionally
    </span>
  )
}

/**
 * Common wrapper for answerable fields (label / required marker / control /
 * help text / error).
 *
 * In `preview` mode the WHOLE shell is `pointer-events-none` so a click in
 * the builder canvas selects the Puck block rather than focusing or opening
 * the control. Applying it at the wrapper — not the bare `<input>` — is
 * load-bearing: composite/portal controls (Radix `Select` trigger + popover,
 * `Checkbox`, native `Date`) would otherwise still open or toggle and steal
 * block selection for half the palette.
 */
export function FieldShell({
  mode,
  htmlFor,
  label,
  required,
  helpText,
  error,
  conditional,
  children,
  className
}: {
  mode: FieldMode
  htmlFor?: string
  label?: string
  required?: boolean
  helpText?: string
  error?: string
  /** When the block has a `showIf` rule, mark it in the builder preview. */
  conditional?: boolean
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('space-y-1.5', mode === 'preview' && 'pointer-events-none select-none', className)}>
      <div className="flex items-center gap-2">
        <FieldLabel htmlFor={htmlFor} label={label} required={required} />
        {mode === 'preview' && conditional && <ConditionalBadge />}
      </div>
      {children}
      <FieldHelpText helpText={helpText} />
      <FieldError error={error} />
    </div>
  )
}
